/**
 * HOW TO PLAY (docs/TUTORIAL.md §6.2): the friendly rulebook, reachable from the main menu, the Football 101 hub,
 * the first-launch offer and the pause menu (ui.overlay = 'how_to_play'). Eight illustrated tabs — football in eight
 * ideas, controls (labelled from the player's real bindings, per device), the counter square, fakes, meters,
 * specials, scoring and the two formats — each with a PRACTICE button that opens the matching lesson.
 *
 * It opens on BASICS (or, paused inside a Football 101 lesson, on the tab about that lesson). ◀ ▶ switch tabs,
 * ▲ ▼ scroll, confirm = PRACTICE THIS, back closes.
 */
import './howto.css';
import { signal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import {
  CHARACTERS,
  characterById,
  POWER,
  STAMINA,
  specialInfo,
  type ButtonName,
  type CharacterId,
} from '@hf/shared';
import { DEVICE_ID } from '@hf/client-core/input/types';
import { useActions, useServices } from '../actions';
import { Art, Glove, Head, Hints, SpecialIcon } from '../components';
import { LAYER, useMenuLayer } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { ui } from '@hf/client-core/ui-model/store';
import type { OverlayId } from '@hf/client-core/ui-model/types';
import { CardArt } from './cards';
import { lessonTitleFor, type LessonId } from '@hf/client-core/ui-model/tutorial/catalog';
import { FAKES, fakeCost } from '@hf/client-core/ui-model/tutorial/fakes';
import { Caps, FakeKeys, pickDevice, useTutorialDevice } from './keys';
import { progress, updateProgress } from '@hf/client-core/ui-model/tutorial/progress';
import { startTutorial } from './start';

/** Where closing HOW TO PLAY returns (the pause menu, or nothing). */
const returnTo = signal<OverlayId>('none');

/** Open HOW TO PLAY over the current screen (menus, hub, pause). */
export const openHowToPlay = (): void => {
  const cur = ui.overlay.peek();
  returnTo.value = cur === 'pause' || cur === 'move_list' ? 'pause' : 'none';
  lastTab.value = startTab();
  ui.overlay.value = 'how_to_play';
  if (!progress.peek().howToPlaySeen) updateProgress({ howToPlaySeen: true });
};

/**
 * BASICS — or, paused in a lesson, the tab that explains it (lesson 7 → COUNTER SQUARE…). Never FORMATS: Game Day
 * mapped there, the least useful page for a newcomer confused mid-game; it opens on BASICS (how a game works).
 */
const startTab = (): number => {
  const t = ui.tutorial.peek();
  const lesson = ui.screen.peek() === 'match' && t?.phase ? t.lessonId : undefined;
  const ix = lesson
    ? TABS.findIndex((x) => x.lesson === lesson && x.id !== 'controls' && x.id !== 'formats')
    : -1;
  return ix >= 0 ? ix : 0;
};

/** The pause menu row to focus when HOW TO PLAY closes back into it (read once by the pause menu). */
let pauseRow: string | null = null;
export const takePauseFocus = (): string | null => {
  const r = pauseRow;
  pauseRow = null;
  return r;
};

const closeHowToPlay = (): void => {
  // back in the pause menu, the focus returns to HOW TO PLAY (it used to land on RESUME)
  pauseRow = returnTo.peek() === 'pause' ? 'howto' : null;
  ui.overlay.value = returnTo.peek();
};

interface Tab {
  id: string;
  title: string;
  lesson: LessonId | null;
}

const TABS: Tab[] = [
  { id: 'basics', title: 'BASICS', lesson: 'snap' },
  { id: 'controls', title: 'CONTROLS', lesson: 'move' },
  { id: 'square', title: 'COUNTER SQUARE', lesson: 'counters' },
  { id: 'fakes', title: 'FAKES', lesson: 'fakes' },
  { id: 'meters', title: 'METERS', lesson: 'meters' },
  { id: 'specials', title: 'SPECIALS', lesson: 'special' },
  { id: 'scoring', title: 'SCORING', lesson: 'scoring' },
  { id: 'formats', title: 'FORMATS', lesson: 'scrimmage' },
];

/** The tab to open on (set by openHowToPlay). */
const lastTab = signal(0);

export function HowToPlayOverlay({ onPractise }: { onPractise?: (lesson: LessonId) => void }) {
  const { audio } = useServices();
  const [tab, setTab] = useState(lastTab.peek());
  const [body, setBody] = useState<HTMLElement | null>(null);
  const more = useMoreBelow(body, tab);
  const device = useTutorialDevice();
  const head = progress.value.head;
  const t = TABS[tab]!;
  const practise = onPractise && t.lesson ? () => onPractise(t.lesson!) : null;
  const go = (i: number): void => {
    const n = (i + TABS.length) % TABS.length;
    lastTab.value = n;
    setTab(n);
    playSfx(audio, UI_SFX.move);
    body?.scrollTo({ top: 0 });
  };
  useMenuLayer(
    (a) => {
      if (a === 'back' || a === 'pause') {
        playSfx(audio, UI_SFX.back);
        closeHowToPlay();
      } else if (a === 'left' || a === 'right') go(tab + (a === 'left' ? -1 : 1));
      else if (a === 'up' || a === 'down')
        body?.scrollBy({ top: (a === 'down' ? 1 : -1) * body.clientHeight * 0.45, behavior: 'smooth' });
      else if (a === 'confirm' && practise) {
        playSfx(audio, UI_SFX.confirm);
        practise();
      }
    },
    true,
    LAYER.OVERLAY,
  );
  return (
    <div class="hf-howto interactive" role="dialog" aria-modal="true" aria-label="How to play">
      <div class="hf-howto__scrim" onClick={closeHowToPlay} />
      <div class="hf-howto__panel">
        <header class="hf-howto__head">
          <div class="hf-howto__title t-display">HOW TO PLAY</div>
          <nav class="hf-howto__tabs" role="tablist">
            {TABS.map((x, i) => (
              <button
                key={x.id}
                role="tab"
                aria-selected={i === tab}
                class={`hf-howto__tab t-broadcast ${i === tab ? 'on' : ''}`}
                onClick={() => go(i)}
              >
                {x.title}
              </button>
            ))}
          </nav>
          <button class="hf-howto__close interactive" aria-label="Close" onClick={closeHowToPlay}>
            ✕
          </button>
        </header>
        <div class="hf-howto__body" ref={setBody} key={t.id}>
          {t.id === 'basics' ? <Basics head={head} device={device} /> : null}
          {t.id === 'controls' ? <Controls /> : null}
          {t.id === 'square' ? <Square head={head} device={device} /> : null}
          {t.id === 'fakes' ? <Fakes device={device} /> : null}
          {t.id === 'meters' ? <Meters /> : null}
          {t.id === 'specials' ? <Specials device={device} /> : null}
          {t.id === 'scoring' ? <Scoring head={head} /> : null}
          {t.id === 'formats' ? <Formats device={device} practice={!!practise} /> : null}
          {/* sticky at the bottom of the scrolling body: a fade while there is more (the MORE BELOW chip lives in
              the footer: over the body it sat on the last card's text — QA round 3) */}
          <div class={`hf-howto__morewrap ${more ? 'on' : ''}`} />
        </div>
        <footer class="hf-howto__foot">
          {practise ? (
            <button class="hf-btn hf-howto__practise interactive" onClick={practise}>
              PRACTICE THIS ▶<small class="t-broadcast">LESSON · {lessonTitleFor(t.lesson!, head)}</small>
            </button>
          ) : (
            <span />
          )}
          <button
            class={`hf-howto__more t-broadcast interactive ${more ? 'on' : ''}`}
            tabIndex={-1}
            aria-hidden={!more}
            onClick={() => body?.scrollBy({ top: body.clientHeight * 0.6, behavior: 'smooth' })}
          >
            ▼ MORE BELOW
          </button>
          <Hints
            items={[
              ['left|right', 'TAB'],
              ['up|down', 'SCROLL'],
              ...(practise ? ([['confirm', 'PRACTICE']] as [string, string][]) : []),
              ['back', 'CLOSE'],
            ]}
          />
        </footer>
      </div>
    </div>
  );
}

/**
 * Is there more of the tab below the fold? (FORMATS' bottom cards were cut off by the footer with no cue.) A fade and
 * a "▼ MORE BELOW" chip show until the player scrolled to the end.
 */
const useMoreBelow = (body: HTMLElement | null, tab: number): boolean => {
  const [more, setMore] = useState(false);
  useEffect(() => {
    if (!body) return;
    const check = (): void => setMore(body.scrollHeight - body.scrollTop - body.clientHeight > 12);
    check();
    const t = setTimeout(check, 120);
    body.addEventListener('scroll', check, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(check);
    ro?.observe(body);
    if (body.firstElementChild) ro?.observe(body.firstElementChild);
    return () => {
      clearTimeout(t);
      body.removeEventListener('scroll', check);
      ro?.disconnect();
    };
  }, [body, tab]);
  return more;
};

/** Mount for the App: shows HOW TO PLAY whenever ui.overlay is 'how_to_play'; PRACTICE opens the lesson. */
export function HowToPlayHost() {
  const actions = useActions();
  if (ui.overlay.value !== 'how_to_play') return null;
  const setup = ui.matchSetup.value;
  const inMatch = ui.screen.value === 'match';
  const inTutorial = inMatch && setup?.mode === 'tutorial';
  const onPractise = inTutorial
    ? (lesson: LessonId) => {
        ui.overlay.value = 'none';
        ui.tutorialCommand({ kind: 'lesson', id: lesson });
      }
    : inMatch
      ? undefined
      : (lesson: LessonId) => {
          ui.overlay.value = 'none';
          startTutorial(lesson, progress.peek().head, actions.startMatch);
        };
  return <HowToPlayOverlay {...(onPractise ? { onPractise } : {})} />;
}

// ── tab bodies ───────────────────────────────────────────────────────────────────────────────────────────────

function Card({
  n,
  title,
  children,
  art,
  tone = '',
}: {
  n?: number | string;
  title: string;
  children: ComponentChildren;
  art?: ComponentChildren;
  tone?: string;
}) {
  return (
    <article class={`hf-hcard ${tone}`}>
      {art ? <div class="hf-hcard__art">{art}</div> : null}
      <div class="hf-hcard__text">
        <h3 class="t-display">
          {n !== undefined ? <span class="hf-hcard__n">{n}</span> : null}
          {title}
        </h3>
        <p>{children}</p>
      </div>
    </article>
  );
}

const OPP: Record<CharacterId, CharacterId> = {
  rookie: 'bigtex',
  bigtex: 'grandpa',
  zorg: 'bigtex',
  grandpa: 'bigtex',
  turbo: 'bigtex',
  dome: 'bigtex',
  rumble: 'bigtex',
  hopper: 'bigtex',
  lodestone: 'bigtex',
};

function FieldStrip({ head, mode }: { head: CharacterId; mode: 'snap' | 'tackle' | 'pass' | 'end' }) {
  return (
    <div class={`hf-strip ${mode}`}>
      {mode === 'end' ? <span class="hf-strip__ez t-broadcast">END ZONE</span> : null}
      <span class="hf-strip__los" />
      {mode === 'snap' ? <span class="hf-strip__ltg" /> : null}
      <Head character={head} expression={mode === 'tackle' ? 'dazed' : 'determined'} class="hf-strip__me" />
      {mode !== 'end' ? (
        <Head
          character={OPP[head]}
          expression={mode === 'tackle' ? 'celebrating' : 'neutral'}
          flip
          class="hf-strip__opp"
        />
      ) : null}
      {mode === 'tackle' ? <span class="hf-strip__burst t-display">THUD!</span> : null}
      {mode === 'pass' ? <span class="hf-strip__arc" /> : null}
      {mode === 'pass' ? <Art keys={['prop/football']} class="hf-strip__ball" /> : null}
      {mode === 'end' ? (
        <span class="hf-strip__postwrap">
          <Art keys={['prop/goalpost', 'prop/goalpost-34']} class="hf-strip__posts" />
        </span>
      ) : null}
    </div>
  );
}

function Basics({ head, device }: { head: CharacterId; device: string }) {
  return (
    <>
      {/* the big picture first: BASICS never said how a game is played or won (it was only under FORMATS) */}
      <div class="hf-howgame" role="note">
        <b class="hf-howgame__title t-display">HOW A GAME WORKS</b>
        <span class="hf-howgame__drives" aria-hidden="true">
          {[1, 2, 3, 4].map((n) => (
            <i key={n} class="t-broadcast">
              {n}
            </i>
          ))}
        </span>
        <span class="hf-howgame__text">
          You take turns: <b>4 drives each</b>, every drive from the other head's <b>40</b>. Score on your
          drive, stop him on his. <b>Most points wins.</b>
        </span>
      </div>
      <BasicsGrid head={head} device={device} />
    </>
  );
}

function BasicsGrid({ head, device }: { head: CharacterId; device: string }) {
  return (
    <div class="hf-howto__grid eight">
      <Card
        n={1}
        title="TWO HEADS, ONE BALL"
        art={
          <div class="hf-duo">
            <Head character={head} expression="determined" class="hf-duo__a" />
            <Art keys={['prop/football']} class="hf-duo__ball" />
            <Head character={OPP[head]} expression="determined" flip class="hf-duo__b" />
          </div>
        }
      >
        You have the ball (<b>OFFENSE</b>) or you stop the one who has it (<b>DEFENSE</b>).
      </Card>
      <Card n={2} title="A PLAY STARTS WITH A SNAP" art={<FieldStrip head={head} mode="snap" />}>
        At the blue line, the <b>LINE OF SCRIMMAGE</b>: press{' '}
        <Caps buttons={['A']} mode="tap" device={device} size="sm" />. The <b>PLAY CLOCK</b> gives you 8 s: at
        :00 the ball snaps itself.
      </Card>
      <Card n={3} title="DEFENSE WAITS FOR GO" art={<RushArt />}>
        After the snap he counts <b>1-2-3 MISSISSIPPI</b>. Crossing the line before <b>GO</b> is{' '}
        <b>OFFSIDE</b>: 5 yards.
      </Card>
      <Card n={4} title="4 TRIES TO GO 10 YARDS" art={<CardArt kind="downs" head={head} />}>
        Reach the <b>yellow line</b>: 4 new tries. Miss: the other head gets the ball.
      </Card>
      <Card n={5} title="RUN IT OR THROW IT" art={<FieldStrip head={head} mode="pass" />}>
        Carry it, or throw it from behind the blue line and catch it yourself.
      </Card>
      <Card n={6} title="A PLAY ENDS WHEN…" art={<FieldStrip head={head} mode="tackle" />}>
        The carrier is <b>tackled</b>, a pass hits the ground, or someone scores.
      </Card>
      <Card n={7} title="EVERY TACKLE HAS AN ANSWER" art={<CardArt kind="square" head={head} />}>
        <b>HURDLE</b> a dive, <b>TRUCK</b> under a leap. On defense: <b>DIVE</b> a truck, <b>LEAP</b> a
        hurdle.
      </Card>
      <Card n={8} title="SCORE!" art={<FieldStrip head={head} mode="end" />}>
        <b>END ZONE</b>: touchdown, 6. Through the posts: field goal, 3.
      </Card>
    </div>
  );
}

/** BASICS card 3: the defender's 3-count, then GO. */
function RushArt() {
  return (
    <div class="hf-rushart" aria-hidden="true">
      {[1, 2, 3].map((n) => (
        <span key={n} class="hf-rushart__n t-display">
          {n}
        </span>
      ))}
      <span class="hf-rushart__go t-display">GO!</span>
    </div>
  );
}

const DEVICES: { id: string; title: string }[] = [
  { id: DEVICE_ID.KB_SOLO, title: 'KEYBOARD' },
  { id: 'pad:0', title: 'CONTROLLER' },
  { id: DEVICE_ID.TOUCH, title: 'TOUCH' },
];

const BUTTON_ROWS: { b: ButtonName; name: string; what: string }[] = [
  { b: 'L', name: 'MOVE BACK', what: 'Hop back. Double-tap to dash.' },
  { b: 'R', name: 'MOVE FORWARD', what: 'Hop forward. Double-tap to dash.' },
  { b: 'U', name: 'JUMP', what: 'Hurdle with the ball. Leap on defense. Hard count before the snap.' },
  { b: 'D', name: 'DOWN', what: 'Hold: truck. In the air: slam (fake jump). Hold + snap: field goal.' },
  { b: 'A', name: 'ACTION', what: 'Does what the moment needs (below).' },
  { b: 'S', name: 'SPECIAL', what: "Full power: your head's special move." },
];

function Controls() {
  const { input } = useServices();
  const [dev, setDev] = useState(() => {
    const d = pickDevice(input);
    return d.startsWith('pad') ? 'pad:0' : d === DEVICE_ID.TOUCH ? DEVICE_ID.TOUCH : DEVICE_ID.KB_SOLO;
  });
  return (
    <div class="hf-controls">
      <div class="hf-controls__devices" role="tablist" aria-label="Device">
        {DEVICES.map((d) => (
          <button
            key={d.id}
            class={`hf-chip ${dev === d.id ? 'gold' : 'dark'} interactive`}
            role="tab"
            aria-selected={dev === d.id}
            onClick={() => setDev(d.id)}
          >
            {d.title}
          </button>
        ))}
      </div>
      <div class="hf-controls__table">
        {BUTTON_ROWS.map((r) => (
          <div class="hf-controls__row" key={r.b}>
            <span class="hf-controls__cap">
              <Caps buttons={[r.b]} mode="tap" device={dev} />
            </span>
            <b class="t-broadcast">{r.name}</b>
            <span>{r.what}</span>
          </div>
        ))}
      </div>
      <div class="hf-controls__action hf-panel">
        <h3 class="t-display">
          <Caps buttons={['A']} mode="tap" device={dev} size="sm" /> ACTION, BY MOMENT
        </h3>
        <ul>
          <li>
            <b class="t-broadcast">BEFORE THE SNAP</b> snap the ball
          </li>
          <li>
            <b class="t-broadcast">BEHIND THE LINE</b> hold to throw, let go to release
          </li>
          <li>
            <b class="t-broadcast">CARRYING</b> stiff-arm
          </li>
          <li>
            <b class="t-broadcast">DEFENSE</b> dive (in the air: leap)
          </li>
          <li>
            <b class="t-broadcast">BALL IN THE AIR</b> catch it or swat it
          </li>
        </ul>
      </div>
    </div>
  );
}

function Square({ head, device }: { head: CharacterId; device: string }) {
  return (
    <div class="hf-howto__split">
      <div class="hf-howto__squarewrap">
        <CardArt kind="square" head={head} />
      </div>
      <div class="hf-howto__stack">
        <Card title="THE CARRIER PICKS: OVER OR UNDER">
          <Caps buttons={['U']} mode="tap" device={device} size="sm" /> <b>HURDLE</b> beats a low DIVE.{' '}
          <Caps buttons={['D']} mode="hold" device={device} size="sm" /> <b>TRUCK</b> beats a high LEAP.
        </Card>
        <Card title="THE DEFENDER PICKS: LOW OR HIGH">
          <Caps buttons={['A']} mode="tap" device={device} size="sm" /> <b>DIVE</b> beats a TRUCK.{' '}
          <Caps buttons={['U', 'A']} mode="then" device={device} size="sm" /> <b>LEAP</b> beats a HURDLE.
        </Card>
        <Card title="TWO MORE">
          <b>SPIN</b> (<Caps buttons={['D', 'A']} mode="chord" device={device} size="sm" />) dodges any lunge.{' '}
          <b>STIFF-ARM</b> beats a standing grab.
        </Card>
        <Card title="READ OR FAKE" tone="pink">
          There is no arrow in a real game: watch his wind-up, or fake him into guessing wrong.
        </Card>
      </div>
    </div>
  );
}

function Fakes({ device }: { device: string }) {
  return (
    <>
      {/* one sentence, not three slogans ("LOOKS REAL AT FIRST · COSTS SOMETHING · PATIENCE BEATS IT" read as
          riddles) */}
      <p class="hf-rules3 sentence">
        <b class="t-broadcast">EVERY FAKE</b> starts like the real move <em class="hf-rules3__arrow">›</em>{' '}
        costs <b>stamina</b> <em class="hf-rules3__arrow">›</em> is beaten by <b>waiting</b> to see the real
        one.
      </p>
      <div class="hf-howto__grid fakes">
        {FAKES.map((f) => (
          <article class="hf-fake" key={f.name}>
            <h3 class="t-display">{f.name}</h3>
            <div class="hf-fake__keys">
              <FakeKeys f={f} device={device} />
            </div>
            <p>Baits {f.baits}.</p>
            <span class={`hf-fake__cost t-broadcast ${f.cost === 0 ? 'free' : ''}`}>{fakeCost(f)}</span>
            <span class="hf-fake__where t-broadcast">{f.drilled ? 'LESSON 8' : 'TRY IT IN PRACTICE'}</span>
          </article>
        ))}
      </div>
    </>
  );
}

function Meters() {
  const costs: [string, number][] = [
    ['DASH', STAMINA.dash],
    ['STUTTER', STAMINA.stutter],
    ['FAKE JUMP', STAMINA.fakeJumpCancel],
    ['SPIN', STAMINA.spin],
    ['PUMP FAKE', STAMINA.pumpFake],
    ['FAKE DIVE', STAMINA.fakeDive],
  ];
  const gains: [string, string][] = [
    ['2 NEW YARDS', '+1'],
    ['TACKLE', `+${POWER.tackle}`],
    ['CATCH', `+${POWER.catch}`],
    ['INTERCEPTION', `+${POWER.interception}`],
    ['SWAT', `+${POWER.swat}`],
    ['A FAKE THAT FOOLS', `+${POWER.bait}`],
  ];
  return (
    <div class="hf-howto__split meters">
      <section class="hf-meterbox stamina">
        <h3 class="t-display">STAMINA</h3>
        <div class="hf-meterbox__bar">
          {Array.from({ length: 10 }, (_, i) => (
            <i key={i} class={i < 6 ? 'on' : ''} />
          ))}
          <span class="hf-meterbox__mark" style={{ left: `${(STAMINA.minFeintCost / STAMINA.max) * 100}%` }}>
            <b class="t-broadcast">{STAMINA.minFeintCost}</b>
          </span>
        </div>
        <ul>
          {costs.map(([n, c]) => (
            <li key={n}>
              <span class="t-broadcast">{n}</span>
              <b class="t-broadcast">−{c}</b>
            </li>
          ))}
        </ul>
        <p>
          Under {STAMINA.minFeintCost} the bar turns <b>RED: MUST BE REAL</b>. No fakes, and your opponent can
          see it. The hard count, hesitation and fast-fall are free. Refills between plays.
        </p>
      </section>
      <section class="hf-meterbox power">
        <h3 class="t-display">POWER</h3>
        <div class="hf-meterbox__power">
          <i style={{ width: '72%' }} />
        </div>
        <ul>
          {gains.map(([n, g]) => (
            <li key={n}>
              <span class="t-broadcast">{n}</span>
              <b class="t-broadcast">{g}</b>
            </li>
          ))}
        </ul>
        <p>
          At {POWER.max}: <b>SPECIAL READY</b>. One special per play.
        </p>
      </section>
    </div>
  );
}

function Specials({ device }: { device: string }) {
  return (
    <>
      <div class="hf-howto__grid four">
        {CHARACTERS.map((c) => {
          const sp = specialInfo(c.signature);
          return (
            <article class="hf-spcard" key={c.id} style={{ '--accent': c.accent }}>
              <div class="hf-spcard__art">
                <Head character={c.id} expression="celebrating" class="hf-spcard__head" />
                <SpecialIcon special={c.signature} class="hf-spcard__icon" />
              </div>
              <span class="hf-spcard__who t-broadcast">{c.name}</span>
              <h3 class="t-display">{sp.name}</h3>
              <p>{sp.blurb}</p>
              <p class="hf-spcard__counter">
                <b class="t-broadcast">COUNTER</b> {sp.counter}
              </p>
            </article>
          );
        })}
      </div>
      <p class="hf-howto__note">
        Full power + <Caps buttons={['S']} mode="tap" device={device} size="sm" />. Every special flashes
        first, so your opponent can react. Specials are OFF in Ranked.
      </p>
    </>
  );
}

function Scoring({ head }: { head: CharacterId }) {
  const rows: [string, string, string][] = [
    ['TOUCHDOWN', '6', 'Carry or catch the ball in the end zone'],
    ['EXTRA POINT', '1', 'After a touchdown: toss it through the posts from the 15'],
    ['TWO-POINT PLAY', '2', 'Or run / throw it in from the 3'],
    ['FIELD GOAL', '3', 'Hold DOWN and snap, then toss it through the posts'],
    ['SAFETY', '2', 'Tackle the carrier in his own end zone'],
    ['PICK SIX', '6', 'Intercept a pass and return it all the way (no extra point)'],
  ];
  return (
    <div class="hf-howto__split">
      <div class="hf-howto__squarewrap">
        <CardArt kind="scoring" head={head} />
        <div class="hf-scoring__strip">
          <FieldStrip head={head} mode="end" />
        </div>
      </div>
      <table class="hf-scoretable">
        <tbody>
          {rows.map(([n, p, w]) => (
            <tr key={n}>
              <th class="t-broadcast">{n}</th>
              <td class="t-display">{p}</td>
              <td>{w}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Formats({ device, practice }: { device: string; practice: boolean }) {
  return (
    <div class="hf-howto__grid two">
      <Card
        title="DRIVES · DEFAULT & RANKED"
        tone="gold"
        art={
          <div class="hf-fmt">
            {[1, 2, 3, 4].map((n) => (
              <span key={n} class="hf-fmt__drive t-broadcast">
                {n}
              </span>
            ))}
          </div>
        }
      >
        4 drives each, taking turns, always from the opponent's 40. Most points wins. Tied: overtime from the
        25.
      </Card>
      <Card
        title="CLASSIC · CASUAL"
        tone="blue"
        art={
          <div class="hf-fmt clock">
            <span class="t-broadcast">2:00</span>
            <span class="t-broadcast">2:00</span>
          </div>
        }
      >
        Two halves of 2:00 with kickoffs, punts (
        <Caps buttons={['S', 'A']} mode="holdWhile" device={device} size="sm" />
        ), onside kicks (hold <Caps buttons={['D']} mode="tap" device={device} size="sm" /> at the release)
        and taking a knee.
      </Card>
      <Card title="FOOTBALL 101" art={<Glove side="home" pose="fist" class="hf-fmt__glove" />}>
        Every rule on these pages has a short lesson
        {practice ? ': press PRACTICE THIS on any tab.' : ' in the FOOTBALL 101 menu.'}
      </Card>
      <Card
        title="THE CPU"
        art={
          <div class="hf-duo small">
            <Head character={characterById('grandpa').id} expression="determined" class="hf-duo__a" />
          </div>
        }
      >
        Four difficulties. Harder CPUs don't get faster: they read the counter square better.
      </Card>
    </div>
  );
}

/** First-launch helper for tests: is HOW TO PLAY up? */
export const howToPlayOpen = (): boolean => ui.overlay.peek() === 'how_to_play';
